import { useEffect, useState } from 'react';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { UserType } from '../../../constants/profileConstants';
import { GuestVisitDetails } from '../../guest/models/guestModel';
import {
  activateGuestEndpoint,
  assignProperRentSecondaryEndpoint,
  cancelVisitEndpoint,
  getAvailableTimeVisitsEndpoint as getGuestAvailableTimesEndpoint,
  getGuestVisitDetailEndpoint,
  updateGuestVisitEndpoint,
} from '../../guest/services/guestService';
import {
  deleteVisitEndpoint,
  getAvailableTimeVisitsEndpoint as getAdminAvailableTimesEndpoint,
  getCancellationReasons,
  updatePropertyVisitsEndpoint,
} from '../../properties/services/propertyService';
import { updateVisitStateEndpoint } from '../services/dashboardService';
import { PropersAssignedVisits } from '../models/dashboardModels';
import { timeToMinutes, toInputDate } from '../components/VisitDetailParts';

export interface VisitTimeSlot {
  minutes: number;
  label: string;
  isBlocked: boolean;
}

export interface AcceptRentVisitForm {
  properId: string;
  visitType: string;
  date: string;
  time: string;
}

// Acciones del detalle de una visita de Renta/Secondary. Reutiliza los
// endpoints de las pantallas anteriores: detalle de guest-visit (aceptar,
// editar fecha/hora, anular) y calendario de la propiedad (reagendar,
// cancelar, marcar como realizada).
// El backend responde 200/201 y, según el endpoint, 204 sin cuerpo.
const OK_STATUSES = [200, 201, 204];
const MISSING_GUEST_VISIT =
  'No se pudo cargar la información del guest de esta visita. Recarga la página e inténtalo de nuevo.';

const useRentVisitActions = (visit: PropersAssignedVisits | undefined) => {
  const { callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const visitId = visit?.id ?? '';
  const propertyId = visit?.propertyInfo.id ?? '';

  const [guestVisit, setGuestVisit] = useState<GuestVisitDetails | null>(null);
  const [timeSlots, setTimeSlots] = useState<VisitTimeSlot[]>([]);
  const [cancellationReasons, setCancellationReasons] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!visitId) return;
    callEndpoint(getGuestVisitDetailEndpoint(visitId, token))
      .then(({ status, data }) => {
        const response = fetchResponseAdapter(data);
        if (status === 200) setGuestVisit(response.data);
      })
      .catch(() => setGuestVisit(null));
    callEndpoint(getCancellationReasons(token))
      .then(({ data }) =>
        setCancellationReasons(Array.isArray(data.data) ? data.data : []),
      )
      .catch(() => setCancellationReasons([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId]);

  const isGuest = guestVisit?.userInfo.type === UserType.GUEST;

  // Fecha/hora con las que ya está agendada la visita (se precargan en los
  // formularios para que se vea cuándo es, no un campo vacío).
  const original = {
    date: toInputDate(visit?.visitInfo.visit_date),
    minutes: timeToMinutes(visit?.visitInfo.visit_time),
    label: visit?.visitInfo.visit_time ?? '',
  };

  // Corre una acción y devuelve true si terminó bien (el error queda en
  // `error` para mostrarlo dentro del panel).
  const run = async (
    action: () => Promise<{ status: number; data: any }[] | boolean>,
    fallback: string,
  ): Promise<boolean> => {
    setError('');
    setIsSubmitting(true);
    try {
      const result = await action();
      if (result === true) return true;
      if (Array.isArray(result)) {
        const failed = result.find(({ status }) => !OK_STATUSES.includes(status));
        if (!failed) return true;
        console.error(`[RentVisit] ${fallback}`, failed.status, failed.data);
        setError(
          `${fetchResponseAdapter(failed.data).message || fallback} (${failed.status})`,
        );
      }
      return false;
    } catch (e) {
      console.error(`[RentVisit] ${fallback}`, e);
      setError(fallback);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // `scope`: 'guest' (visita pendiente) o 'admin' (visita ya agendada).
  const loadTimeSlots = async (date: string, scope: 'guest' | 'admin') => {
    setTimeSlots([]);
    if (!date || !propertyId) return;
    const { status, data } = await callEndpoint(
      scope === 'guest'
        ? getGuestAvailableTimesEndpoint(propertyId, date, token)
        : getAdminAvailableTimesEndpoint(propertyId, date, token),
    );
    const response = fetchResponseAdapter(data);
    if (status !== 200 || !Array.isArray(response.data)) {
      setError(response.message);
      return;
    }
    const slots: VisitTimeSlot[] = response.data.map((slot: any) => ({
      minutes: slot.minutes,
      label: slot.time_value ?? slot.time,
      // El horario de esta misma visita aparece ocupado: se deja elegible.
      isBlocked:
        !!slot.is_blocked &&
        !(date === original.date && slot.minutes === original.minutes),
    }));
    if (
      date === original.date &&
      original.minutes !== undefined &&
      !slots.some((slot) => slot.minutes === original.minutes)
    ) {
      slots.unshift({
        minutes: original.minutes,
        label: original.label,
        isBlocked: false,
      });
    }
    setTimeSlots(slots);
  };

  const updateSchedule = (visitType: string, date: string, time: string) =>
    run(async () => {
      const res = await callEndpoint(
        updateGuestVisitEndpoint(
          visitId,
          { visit_type: visitType, date, start_time: Number(time) },
          token,
        ),
      );
      return [res];
    }, 'No fue posible actualizar la visita');

  const accept = (form: AcceptRentVisitForm) =>
    run(async () => {
      if (!guestVisit) {
        setError(MISSING_GUEST_VISIT);
        return false;
      }
      if (!form.properId) {
        setError('Selecciona un proper.');
        return false;
      }
      const results: { status: number; data: any }[] = [];
      const typeChanged = form.visitType !== guestVisit.visitInfo.visit_type;
      const hasNewSchedule =
        !!form.date &&
        form.time !== '' &&
        (form.date !== original.date ||
          Number(form.time) !== original.minutes);
      if (hasNewSchedule || typeChanged) {
        // Para cambiar sólo el tipo se reenvía la fecha/hora actuales.
        const date = hasNewSchedule ? form.date : original.date;
        const startTime = hasNewSchedule
          ? Number(form.time)
          : (guestVisit.visitInfo.visit_time_minutes ?? original.minutes);
        if (!date || startTime === undefined) {
          setError('Para cambiar el tipo elige también la fecha y la hora.');
          return false;
        }
        const res = await callEndpoint(
          updateGuestVisitEndpoint(
            visitId,
            { visit_type: form.visitType, date, start_time: startTime },
            token,
          ),
        );
        results.push(res);
        if (!OK_STATUSES.includes(res.status)) return results;
      }
      if (isGuest) {
        const res = await callEndpoint(
          activateGuestEndpoint(guestVisit.guestInfo.id, form.properId, token),
        );
        results.push(res);
        if (!OK_STATUSES.includes(res.status)) return results;
      }
      results.push(
        await callEndpoint(
          assignProperRentSecondaryEndpoint(
            visitId,
            { proper_id: form.properId },
            token,
          ),
        ),
      );
      return results;
    }, 'No fue posible aceptar la visita');

  const reschedule = (date: string, time: string, properId: string) =>
    run(
      async () => [
        await callEndpoint(
          updatePropertyVisitsEndpoint(
            { date, start_time: Number(time), proper_id: properId },
            visitId,
            token,
          ),
        ),
      ],
      'No fue posible reagendar la visita',
    );

  // Visita pendiente: se anula desde el guest, con motivo y comentario.
  const cancelPending = (reason: string, explain: string) =>
    run(async () => {
      if (!guestVisit) {
        setError(MISSING_GUEST_VISIT);
        return false;
      }
      return [
        await callEndpoint(
          cancelVisitEndpoint(
            guestVisit.guestInfo.id,
            visitId,
            token,
            reason,
            explain,
          ),
        ),
      ];
    }, 'No fue posible anular la visita');

  // Visita agendada: se cancela con motivo desde el calendario de la propiedad.
  const cancelScheduled = (reason: string, explain: string) =>
    run(
      async () => [
        await callEndpoint(deleteVisitEndpoint(visitId, token, reason, explain)),
      ],
      'No fue posible cancelar la visita',
    );

  const markAsDone = () =>
    run(
      async () => [
        await callEndpoint(updateVisitStateEndpoint(visitId, true, token)),
      ],
      'No fue posible marcar la visita como realizada',
    );

  return {
    guestVisit,
    isGuest,
    original,
    timeSlots,
    cancellationReasons,
    error,
    setError,
    isSubmitting,
    loadTimeSlots,
    updateSchedule,
    accept,
    reschedule,
    cancelPending,
    cancelScheduled,
    markAsDone,
  };
};

export default useRentVisitActions;
